import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// 1. Import Supabase client instead of Firebase
import { supabase } from './supabaseClient'; 
import { Mail, Lock, User, ArrowRight, Loader2, AlertCircle, CheckCircle, Chrome } from 'lucide-react';

  import posthog from 'posthog-js';

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // --- AUTH LISTENER & REDIRECT LOGIC ---
  useEffect(() => {
    // Supabase listener returns { data: { subscription } }
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      
      if (session?.user 
	      /*
	       && 
               (event === 'SIGNED_IN' ||
	       event === 'INITIAL_SESSION')
	      */
      ) {

	      if (session?.user) {
  posthog.identify(session.user.id, {
    email: session.user.email,
    full_name: session.user.user_metadata?.full_name
  });
        }

        // 2. Determine Redirect Destination
        const destination = location.state?.from || '/console';
        navigate(destination, { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location]);

  // --- HANDLERS ---

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // Supabase redirects to their site then back to yours
          redirectTo: window.location.origin + '/console' 
        }
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      } 
      else if (mode === 'signup') {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            // Supabase stores extra fields in user_metadata
            data: {
              full_name: fullName,
            }
          }
        });
        if (error) throw error;
        
        // Note: If Email Confirmation is ON in Supabase dashboard, 
        // session will be null here and user must check email.
        setSuccessMsg("Check your email for the confirmation link!");
        setLoading(false);
      } 
      else if (mode === 'forgot') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          // This is where the user is sent after clicking the email link
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setSuccessMsg("Password reset link sent to your email.");
        setLoading(false);
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // --- UI RENDER HELPERS (Unchanged) ---
  const inputClass = "w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white focus:ring-2 focus:ring-blue-600 outline-none transition-all placeholder:text-slate-600";
  const labelClass = "block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1";

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4 font-sans text-slate-200">
      <div className="fixed top-[-20%] left-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[128px] pointer-events-none" />
      <div className="fixed bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[128px] pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="bg-slate-950/80 backdrop-blur-md border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
          <div className="h-1 w-full bg-gradient-to-r from-blue-600 to-purple-600" />
          <div className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white tracking-tight mb-2">
                {mode === 'login' && 'Welcome Back'}
                {mode === 'signup' && 'Create Account'}
                {mode === 'forgot' && 'Reset Password'}
              </h1>
              <p className="text-slate-400 text-sm">
                {mode === 'login' && 'Enter your credentials to access your workspace.'}
                {mode === 'signup' && 'Join to start shipping Roblox games in days not months.'}
                {mode === 'forgot' && 'Enter your email to receive a reset link.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'signup' && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className={labelClass}>Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5 text-slate-500" size={18} />
                    <input
                      type="text"
                      required
                      className={inputClass}
                      placeholder="John Doe"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className={labelClass}>Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 text-slate-500" size={18} />
                  <input
                    type="email"
                    required
                    className={inputClass}
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              {mode !== 'forgot' && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">Password</label>
                    {mode === 'login' && (
                      <button
                        type="button"
                        onClick={() => { setMode('forgot'); setError(null); }}
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        Forgot?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3.5 text-slate-500" size={18} />
                    <input
                      type="password"
                      required
                      className={inputClass}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-3 text-red-400 bg-red-950/30 p-3 rounded-lg border border-red-900/50 text-sm">
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {successMsg && (
                <div className="flex items-start gap-3 text-green-400 bg-green-950/30 p-3 rounded-lg border border-green-900/50 text-sm">
                  <CheckCircle size={18} className="mt-0.5 shrink-0" />
                  <span>{successMsg}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg shadow-[0_0_15px_rgba(37,99,235,0.3)] transition-all hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : (
                   <>
                     {mode === 'login' && 'Sign In'}
                     {mode === 'signup' && 'Create Account'}
                     {mode === 'forgot' && 'Send Reset Link'}
                     {!loading && <ArrowRight size={18} />}
                   </>
                )}
              </button>
            </form>

          {/*  {mode !== 'forgot' && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-800"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-[#0b101f] text-slate-500">Or continue with</span>
                  </div>
                </div>
               
		        <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full py-3 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 mb-6"
                >
                  <Chrome size={20} className="text-white" />
                  <span>Google</span>
                </button>
	
			    

              </>
            )} */}

            <div className="text-center mt-6">
              {mode === 'login' && (
                <p className="text-sm text-slate-400">
                  Don't have an account?{' '}
                  <button onClick={() => { setMode('signup'); setError(null); }} className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">
                    Sign up
                  </button>
                </p>
              )}
              {mode === 'signup' && (
                <p className="text-sm text-slate-400">
                  Already have an account?{' '}
                  <button onClick={() => { setMode('login'); setError(null); }} className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">
                    Sign in
                  </button>
                </p>
              )}
              {mode === 'forgot' && (
                <button
                  onClick={() => { setMode('login'); setError(null); setSuccessMsg(null); }}
                  className="text-sm text-slate-400 hover:text-white transition-colors flex items-center justify-center gap-2 w-full"
                >
                  <ArrowRight size={14} className="rotate-180" /> Back to Sign In
                </button>
              )}
            </div>
          </div>
        </div>
        <p className="text-center text-slate-600 text-xs mt-8">
          &copy; {new Date().getFullYear()} Hollowpad. All rights reserved.
        </p>
      </div>
    </div>
  );
}
